import { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';

const riskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  asset_or_process: z.string().optional(),
  cause: z.string().optional(),
  consequence: z.string().optional(),
  impact: z.number().min(1).max(5),
  likelihood: z.number().min(1).max(5),
  status: z.enum(['identified', 'assessing', 'treating', 'monitoring', 'closed']),
  treatment: z.string().optional(),
  notes: z.string().optional(),
  residual_score: z.number().optional(),
});

type RiskFormData = z.infer<typeof riskSchema>;

interface RiskEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  risk: any;
  onSave: (id: string, data: any) => void;
  isSaving?: boolean;
}

export function RiskEditDialog({ open, onOpenChange, risk, onSave, isSaving }: RiskEditDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RiskFormData>({
    resolver: zodResolver(riskSchema),
    defaultValues: {
      impact: 3,
      likelihood: 3,
      status: 'identified',
    },
  });

  useEffect(() => {
    if (risk && open) {
      reset({
        title: risk.title || '',
        asset_or_process: risk.asset_or_process || '',
        cause: risk.cause || '',
        consequence: risk.consequence || '',
        impact: risk.impact || 3,
        likelihood: risk.likelihood || 3,
        status: risk.status || 'identified',
        treatment: risk.treatment || '',
        notes: risk.notes || '',
        residual_score: risk.residual_score || undefined,
      });
    }
  }, [risk, open, reset]);

  const watchedLikelihood = watch('likelihood');
  const watchedImpact = watch('impact');
  const riskScore = watchedLikelihood * watchedImpact;

  const getRiskLevel = (score: number) => {
    if (score >= 15) return { label: 'High', color: 'bg-destructive text-destructive-foreground' };
    if (score >= 10) return { label: 'Medium', color: 'bg-warning text-warning-foreground' };
    if (score >= 5) return { label: 'Low', color: 'bg-compliant text-compliant-foreground' };
    return { label: 'Very Low', color: 'bg-muted text-muted-foreground' };
  };

  const riskLevel = getRiskLevel(riskScore);

  const onSubmit = (data: RiskFormData) => {
    onSave(risk.id, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Risk</DialogTitle>
          <DialogDescription>
            Update risk details and assessment
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Risk Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter risk title"
            />
            {errors.title && (
              <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="asset_or_process">Asset or Process</Label>
            <Input
              id="asset_or_process"
              {...register('asset_or_process')}
              placeholder="What asset or process is at risk?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cause">Cause</Label>
              <Textarea
                id="cause"
                {...register('cause')}
                placeholder="What could cause this risk?"
                className="min-h-[80px]"
              />
            </div>
            <div>
              <Label htmlFor="consequence">Consequence</Label>
              <Textarea
                id="consequence"
                {...register('consequence')}
                placeholder="What would happen if this risk occurred?"
                className="min-h-[80px]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="likelihood">Likelihood (1-5) *</Label>
              <Select
                value={watchedLikelihood?.toString()}
                onValueChange={(value) => setValue('likelihood', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Unlikely</SelectItem>
                  <SelectItem value="2">2 - Unlikely</SelectItem>
                  <SelectItem value="3">3 - Possible</SelectItem>
                  <SelectItem value="4">4 - Likely</SelectItem>
                  <SelectItem value="5">5 - Very Likely</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="impact">Impact (1-5) *</Label>
              <Select
                value={watchedImpact?.toString()}
                onValueChange={(value) => setValue('impact', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Negligible</SelectItem>
                  <SelectItem value="2">2 - Minor</SelectItem>
                  <SelectItem value="3">3 - Moderate</SelectItem>
                  <SelectItem value="4">4 - Major</SelectItem>
                  <SelectItem value="5">5 - Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Risk Score</div>
              <div className="text-2xl font-bold">{riskScore}</div>
            </div>
            <Badge className={riskLevel.color}>{riskLevel.label}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={watch('status')}
                onValueChange={(value: any) => setValue('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="identified">Identified</SelectItem>
                  <SelectItem value="assessing">Assessing</SelectItem>
                  <SelectItem value="treating">Treating</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="treatment">Treatment</Label>
              <Select
                value={watch('treatment') || 'none'}
                onValueChange={(value) => setValue('treatment', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="accept">Accept</SelectItem>
                  <SelectItem value="mitigate">Mitigate</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="avoid">Avoid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="residual_score">Residual Score (after treatment)</Label>
            <Input
              id="residual_score"
              type="number"
              min="0"
              max="25"
              {...register('residual_score', { valueAsNumber: true })}
              placeholder="Optional"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Additional notes and observations"
              className="min-h-[100px]"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
